import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const ContactoPage = () => {
  const heroRef = useRef(null);
  const formRef = useRef(null);
  const formInView = useInView(formRef, { once: true, amount: 0.2 });

  // Parallax effect for hero
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 500], [0, 120]);
  const heroImageScale = useTransform(scrollY, [0, 500], [1, 1.2]);

  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    tipoProyecto: '',
    metrosCuadrados: '',
    barrio: '',
    mensaje: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Set document metadata
  useEffect(() => {
    document.title = 'Contacto PLADUREX | Presupuesto Gratis en 24h | Pladur Terrassa';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Contacta con PLADUREX para tu proyecto de pladur en Terrassa. Presupuesto gratis en 24 horas. ☎ 629 501 941 | WhatsApp disponible. Servicio en Terrassa y Vallès.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'contacto pladur Terrassa, presupuesto pladur gratis, teléfono pladur Terrassa, WhatsApp pladur, solicitar presupuesto pladur');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/contacto`);

    // Add JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contacto PLADUREX - Presupuesto Gratis",
      "description": "Contacta con PLADUREX para solicitar presupuesto gratis de instalación de pladur en Terrassa. Respuesta en 24 horas",
      "url": `${import.meta.env.VITE_SITE_URL}/contacto`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${import.meta.env.VITE_SITE_URL}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contacto",
            "item": `${import.meta.env.VITE_SITE_URL}/contacto`
          }
        ]
      },
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "PLADUREX",
        "telephone": "+34629501941",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Terrassa",
          "addressRegion": "Barcelona",
          "addressCountry": "ES"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Terrassa"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Vallès Occidental"
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validación
    if (!formData.nombre || !formData.telefono || !formData.tipoProyecto) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('nombre', formData.nombre);
      formBody.append('telefono', formData.telefono);
      formBody.append('tipoProyecto', formData.tipoProyecto);
      formBody.append('metrosCuadrados', formData.metrosCuadrados);
      formBody.append('barrio', formData.barrio);
      formBody.append('mensaje', formData.mensaje);

      const response = await fetch('https://readdy.ai/api/form/d61fto4cm4ll99guphn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          tipoProyecto: '',
          metrosCuadrados: '',
          barrio: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-deep-green min-h-screen">
      <Header />
      <WhatsAppButton />
      <ScrollToTopButton />

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[35vh] min-h-[350px] flex items-center justify-center overflow-hidden pt-20">
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroImageY }}
        >
          <motion.img
            src="https://public.readdy.ai/ai/img_res/ae5db307a1848d63663435ea198b06ee.jpg"
            alt="Contacto"
            className="w-full h-[120%] object-cover object-top"
            style={{ scale: heroImageScale }}
          />
          <div className="absolute inset-0 bg-deep-green/70"></div>
        </motion.div>

        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="text-sm text-gray-300 mb-4">
              <Link to="/" className="hover:text-primary-yellow transition-colors">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary-yellow">Contacto</span>
            </nav>
            <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-text-light mb-3">
              Contacta con PLADUREX
            </h1>
            <p className="text-xl text-gray-300">
              Presupuesto gratis en 24 horas
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={formRef} className="py-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid lg:grid-cols-[60%_40%] gap-12">
            {/* Form Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-card-green rounded-xl p-8 lg:p-10">
                <h2 className="font-display text-3xl font-bold text-text-light mb-6">
                  Solicita tu Presupuesto
                </h2>
                <form id="contacto-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  {/* Nombre */}
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-semibold text-text-light mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-green border border-card-green focus:border-primary-yellow rounded-md text-text-light placeholder-gray-500 outline-none transition-colors text-sm"
                      placeholder="Tu nombre completo"
                    />
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-semibold text-text-light mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-green border border-card-green focus:border-primary-yellow rounded-md text-text-light placeholder-gray-500 outline-none transition-colors text-sm"
                      placeholder="+34 600 000 000"
                    />
                  </div>

                  {/* Tipo de Proyecto */}
                  <div>
                    <label htmlFor="tipoProyecto" className="block text-sm font-semibold text-text-light mb-2">
                      Tipo de Proyecto *
                    </label>
                    <select
                      id="tipoProyecto"
                      name="tipoProyecto"
                      value={formData.tipoProyecto}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-green border border-card-green focus:border-primary-yellow rounded-md text-text-light outline-none transition-colors text-sm cursor-pointer"
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="Tabiques de Pladur">Tabiques de Pladur</option>
                      <option value="Trasdosados">Trasdosados</option>
                      <option value="Falsos Techos">Falsos Techos</option>
                      <option value="Aislamiento Acústico">Aislamiento Acústico</option>
                      <option value="Aislamiento Térmico">Aislamiento Térmico</option>
                      <option value="Pladur Antihumedad">Pladur Antihumedad</option>
                      <option value="Reforma Integral">Reforma Integral</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  {/* Metros Cuadrados */}
                  <div>
                    <label htmlFor="metrosCuadrados" className="block text-sm font-semibold text-text-light mb-2">
                      m² Aproximados
                    </label>
                    <input
                      type="number"
                      id="metrosCuadrados"
                      name="metrosCuadrados"
                      value={formData.metrosCuadrados}
                      onChange={handleChange}
                      min="1"
                      className="w-full px-4 py-3 bg-deep-green border border-card-green focus:border-primary-yellow rounded-md text-text-light placeholder-gray-500 outline-none transition-colors text-sm"
                      placeholder="Ej: 20"
                    />
                  </div>

                  {/* Barrio */}
                  <div>
                    <label htmlFor="barrio" className="block text-sm font-semibold text-text-light mb-2">
                      Barrio / Zona
                    </label>
                    <input
                      type="text"
                      id="barrio"
                      name="barrio"
                      value={formData.barrio}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-deep-green border border-card-green focus:border-primary-yellow rounded-md text-text-light placeholder-gray-500 outline-none transition-colors text-sm"
                      placeholder="Ej: Centre, Ca n'Aurell..."
                    />
                  </div>

                  {/* Mensaje */}
                  <div>
                    <label htmlFor="mensaje" className="block text-sm font-semibold text-text-light mb-2">
                      Mensaje
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-deep-green border border-card-green focus:border-primary-yellow rounded-md text-text-light placeholder-gray-500 outline-none transition-colors resize-none text-sm"
                      placeholder="Cuéntanos más sobre tu proyecto... (máximo 500 caracteres)"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">{formData.mensaje.length}/500 caracteres</p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-primary-yellow text-deep-green font-bold text-lg rounded-lg hover:shadow-[0_0_30px_rgba(255,212,0,0.5)] hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar Solicitud
                        <i className="ri-send-plane-fill"></i>
                      </>
                    )}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-accent-lime/20 border border-accent-lime rounded-lg"
                    >
                      <p className="text-accent-lime text-sm font-semibold flex items-center gap-2">
                        <i className="ri-checkbox-circle-fill"></i>
                        ¡Mensaje enviado! Te contactaremos pronto.
                      </p>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-red-500/20 border border-red-500 rounded-lg"
                    >
                      <p className="text-red-400 text-sm font-semibold flex items-center gap-2">
                        <i className="ri-error-warning-fill"></i>
                        Error al enviar. Por favor, inténtalo de nuevo.
                      </p>
                    </motion.div>
                  )}
                </form>
              </div>
            </motion.div>

            {/* Info Column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Teléfono */}
              <div className="bg-card-green rounded-xl p-6 hover:scale-105 transition-transform duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary-yellow flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-deep-green text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-text-light mb-2">Teléfono</h3>
                    <a
                      href="tel:+34629501941"
                      className="text-primary-yellow hover:text-accent-lime transition-colors font-semibold text-xl"
                    >
                      +34 629 501 941
                    </a>
                    <p className="text-gray-400 text-sm mt-1">Llamar ahora</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="bg-card-green rounded-xl p-6 hover:scale-105 transition-transform duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-accent-lime flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-deep-green text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-text-light mb-2">WhatsApp</h3>
                    <a
                      href="https://wa.me/34629501941"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-lime hover:text-primary-yellow transition-colors font-semibold text-xl"
                    >
                      +34 629 501 941
                    </a>
                    <p className="text-gray-400 text-sm mt-1">Enviar mensaje</p>
                  </div>
                </div>
              </div>

              {/* Horario */}
              <div className="bg-card-green rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-yellow to-accent-lime flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-deep-green text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-text-light mb-2">Horario</h3>
                    <p className="text-gray-300">Consultar</p>
                    <p className="text-gray-400 text-sm mt-1">Lunes a Viernes</p>
                  </div>
                </div>
              </div>

              {/* Zona de Servicio */}
              <div className="bg-card-green rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-accent-lime to-primary-yellow flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-deep-green text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-text-light mb-2">Área de Servicio</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Terrassa, Vallès Occidental y Barcelona
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Quick Links */}
              <div className="bg-card-green/50 rounded-xl p-6 border border-primary-yellow/20">
                <h3 className="text-lg font-bold text-text-light mb-4">Enlaces útiles</h3>
                <div className="space-y-3">
                  <Link
                    to="/servicios"
                    className="flex items-center gap-3 text-gray-300 hover:text-primary-yellow transition-colors"
                  >
                    <i className="ri-tools-line text-primary-yellow"></i>
                    <span className="text-sm">Ver todos nuestros servicios de pladur</span>
                  </Link>
                  <Link
                    to="/empresa"
                    className="flex items-center gap-3 text-gray-300 hover:text-primary-yellow transition-colors"
                  >
                    <i className="ri-building-line text-primary-yellow"></i>
                    <span className="text-sm">Conoce nuestra empresa y metodología</span>
                  </Link>
                  <Link
                    to="/faq"
                    className="flex items-center gap-3 text-gray-300 hover:text-primary-yellow transition-colors"
                  >
                    <i className="ri-question-line text-primary-yellow"></i>
                    <span className="text-sm">Preguntas frecuentes sobre pladur</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactoPage;
